import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Trophy, 
  Clock, 
  ChevronRight,
  Loader2,
  CheckCircle2,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

export function GoldenInfoWidget({ language = 'fr' }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/golden-infos/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching golden info stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchStats();
    }
  }, [token]);

  if (loading) {
    return (
      <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card 
      className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/golden-info')}
      data-testid="golden-info-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <Trophy className="w-5 h-5" />
            Info d'Or
          </span>
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
            Voir tout <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {/* Total */}
          <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
              <Trophy className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          
          {/* À traiter */}
          <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
              <Clock className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.a_traiter}</p>
            <p className="text-xs text-slate-500">À traiter</p>
          </div>
          
          {/* Signés */}
          <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.signe}</p>
            <p className="text-xs text-slate-500">Signés</p>
          </div>
          
          {/* CA Signé */}
          <div className="text-center p-3 bg-green-100/50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
              <Building2 className="w-4 h-4" />
            </div>
            <p className="text-lg font-bold text-green-700 dark:text-green-400">{formatCurrency(stats.total_ca_signe)}</p>
            <p className="text-xs text-green-600">CA Signé</p>
          </div>
        </div>
        
        {stats.a_traiter > 0 && (
          <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {stats.a_traiter} opportunité{stats.a_traiter > 1 ? 's' : ''} en attente de traitement
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
